import { NUMBER_LOCALE } from "./numberLocale";

/**
 * The number formats of the KPI pictures (plan 2026-10-16-gauge-and-bullet-
 * redesign, D2). Their own module, not `optionBuilder.ts`: that file imports
 * `kpiPictures.ts`, so a format living there would close an import cycle. The
 * tile's own number format (`formatTooltipValue`) is handed in by the builder
 * as `formatValue`, as before.
 */

/** A real minus, not a hyphen: the gap pill and a negative headline read as a sign. */
const MINUS = "−";

/**
 * The big number and the footer's plan: a currency in whole units
 * (`€468,426`) because cents on a headline are noise; from a million on, the
 * tile's own `€1.23M`. Every other format is the tile's own.
 */
export function formatHeadline(value: number, format: string | undefined, formatValue: (v: number) => string): string {
  if (format === "currency" && Math.abs(value) < 1_000_000) {
    const whole = Math.round(Math.abs(value)).toLocaleString(NUMBER_LOCALE);
    return `${value < 0 && whole !== "0" ? MINUS : ""}€${whole}`;
  }
  return formatValue(value);
}

const COMPACT = new Intl.NumberFormat(NUMBER_LOCALE, { notation: "compact", maximumSignificantDigits: 3 });

/** Axis ends and the target label: `€958K`, `€1.23M`, `12.5K`, `40%`. */
export function formatCompact(value: number, format: string | undefined): string {
  if (format === "fraction") return `${COMPACT.format(value * 100)}%`;
  if (format === "percentage") return `${COMPACT.format(value)}%`;
  const sign = value < 0 ? MINUS : "";
  const body = COMPACT.format(Math.abs(value));
  return format === "currency" ? `${sign}€${body}` : `${sign}${body}`;
}

/** "58.7%": the actual as a share of the plan, always one decimal. */
export function formatShare(actual: number, plan: number): string {
  return `${((actual / plan) * 100).toFixed(1)}%`;
}

/**
 * The gap between the actual and the plan, unsigned, in the unit a reader
 * compares in: a fraction or percentage metric's gap is in points (`2.4pp`),
 * because "−2.4%" would read as relative. The sign is the caller's word
 * ("to target", "above target"), never the formatter's `€-…`.
 */
export function formatGapMagnitude(
  gap: number,
  format: string | undefined,
  formatValue: (v: number) => string,
): string {
  const abs = Math.abs(gap);
  if (format === "fraction") return `${(abs * 100).toFixed(1)}pp`;
  if (format === "percentage") return `${abs.toFixed(1)}pp`;
  return formatHeadline(abs, format, formatValue);
}

export { MINUS };
